import { useEffect, useState } from 'react'
import type { ExtraMenuItemAdmin } from 'api/generated'
import { Header } from 'components/layout/header'
import { Main } from 'components/layout/main'
import { Search } from 'components/search'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Switch } from 'components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table'
import { Loader, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  deleteExtraMenuItem,
  fetchExtraMenuItems,
  updateExtraMenuItem,
} from 'store/extraMenuItems/extraMenuItemsSlice'
import { useAppDispatch, useAppSelector } from 'store/store'
import ExtraMenuItemDialog from 'ui/components/ExtraMenuItemDialog'

const isExternal = (url: string) => /^https?:\/\//.test(url)

/** Voci aggiuntive del menu hamburger di sito e app (`/admin/extra-menu-items`). */
const ExtraMenuItemsPage = () => {
  const dispatch = useAppDispatch()
  const { loading, items } = useAppSelector((s) => s.extraMenuItems)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ExtraMenuItemAdmin | null>(null)

  useEffect(() => {
    dispatch(fetchExtraMenuItems())
  }, [dispatch])

  const nextPosition = items.length
    ? Math.max(...items.map((item) => item.position)) + 1
    : 1

  const openDialog = (item: ExtraMenuItemAdmin | null) => {
    setEditing(item)
    setDialogOpen(true)
  }

  return (
    <>
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
        </div>
      </Header>
      <Main>
        <div className='mb-4 flex flex-wrap items-center justify-between gap-4'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>
              Menu aggiuntivo
            </h2>
            <p className='text-muted-foreground'>
              Voci in più nel menu hamburger di sito e app, in ordine di
              posizione. Le app le leggono all&apos;apertura: le modifiche
              arrivano dalla sessione successiva.
            </p>
          </div>
          <Button onClick={() => openDialog(null)}>
            <Plus className='mr-1 h-4 w-4' /> Nuova voce
          </Button>
        </div>
        {loading && !items.length ? (
          <Loader className='h-5 w-5 animate-spin' />
        ) : (
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-20'>Posizione</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Link</TableHead>
                  <TableHead className='w-24'>Attiva</TableHead>
                  <TableHead className='w-24' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length ? (
                  items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.position}</TableCell>
                      <TableCell className='font-medium'>
                        {item.label}
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <Badge variant='outline'>
                            {isExternal(item.url) ? 'Esterno' : 'Interno'}
                          </Badge>
                          <span className='max-w-md truncate'>{item.url}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={item.active}
                          aria-label={`Attiva ${item.label}`}
                          onCheckedChange={(active) =>
                            dispatch(
                              updateExtraMenuItem({
                                id: item.id,
                                input: { active },
                              })
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div className='flex justify-end gap-1'>
                          <Button
                            size='icon'
                            variant='ghost'
                            aria-label={`Modifica ${item.label}`}
                            onClick={() => openDialog(item)}
                          >
                            <Pencil className='h-4 w-4' />
                          </Button>
                          <Button
                            size='icon'
                            variant='ghost'
                            aria-label={`Elimina ${item.label}`}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Eliminare la voce "${item.label}"? Per nasconderla e basta, disattivala.`
                                )
                              ) {
                                dispatch(deleteExtraMenuItem(item.id))
                              }
                            }}
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className='h-24 text-center text-muted-foreground'
                    >
                      Nessuna voce: il menu dell&apos;app è quello di sempre.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {dialogOpen && (
          <ExtraMenuItemDialog
            key={editing?.id ?? 'new'}
            item={editing}
            nextPosition={nextPosition}
            onOpenChange={setDialogOpen}
          />
        )}
      </Main>
    </>
  )
}

export default ExtraMenuItemsPage
